import React from 'react';
import CustomModal from '../../../../components/Modal/CustomModal';
import CustomCard from '../../../../components/CustomCard';
import CustomTooltip from '../../../../components/CustomTooltip';
import CustomAddChipSearch from '../../../../components/CustomAddChipSearch';
import CustomDateTimePicker from '../../../../components/CustomDateTimePicker';
import { CustomTextField } from '../../../../components/CustomTextField';
import {
    IconChipCircleTools,
    IconEntryDevice,
    IconExitDevice,
    ImgCheckInHeader,
} from '../../../../components/buttons/IconButtons';
import MaterialCheckOutModalApp from '../Functionality/MaterialCheckOutModalApp';
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    employeeOpenEntryListToLabel,
    getDateFormat,
    getModelSimpleSearch,
} from '../../../../Apis/functions';
import { SERVICE_ADVANCED_SEARCH_SIMPLE_SEARCH_USER } from '../../../../reduxToolkit/Commons/CommonsApi';
import { TABLES } from '../../../../Apis/Tables';
import { STATUS_MODEL } from '../../../../enums/statusEnum';
import { URL } from '../../../../Apis/Constants';

/**
 * @component MaterialCheckOutModal
 * @description Exit of a material that is still inside, opened from the equipment and material tab.
 *
 * It works as the check-out of the contractors tab, but the material is the one that is already known
 * and what is searched is the employee that takes it out, which is only searched among the employees of
 * the contractor that are inside: registering the exit of someone that never registered an entry leaves
 * an exit with no entry to pair with, and the employees outside card builds its total time from that pair.
 *
 * @param {boolean} show - Whether the modal is open.
 * @param {Function} onHide - Closes the modal.
 * @param {Function} [onSuccess] - Executed once the exit is stored.
 * @param {Object} itemSelected - Row selected in the table, its id is the material access log id.
 */
const MaterialCheckOutModal = (props) => {
    const { onHide, onSuccess, itemSelected } = props;

    const { t, material, detail, item, handleChange, selectedList, setSelectedList, emptyFields, handleCheckOut } =
        MaterialCheckOutModalApp({ itemSelected, onHide, onSuccess });

    return (
        <CustomModal
            {...props}
            size='lg'
            display={'m-0px'}
            classNameBody='m-0 p-0'
            onHide={onHide}
            onConfirm={handleCheckOut}
            topLeftHeader={
                <div className={'d-flex align-items-center pt-2'}>
                    <IconExitDevice className={'w-32px h-32px'} />
                    <p className={'fs-modal-title ibl-font-w-600 font-color-general-950 ml-2 text-uppercase'}>
                        {t('check_out')}
                    </p>
                </div>
            }
        >
            <CustomCard classCard={'card-border-8px'} classContent={'p-0'}>
                <ImgCheckInHeader className={'w-100'} />
                <div className={'row my-3'}>
                    <div className={'col-12 d-flex justify-content-between'}>
                        <div className={'d-flex justify-content-start align-items-center minw-0'}>
                            {material?.fileUploadDto ? (
                                <img
                                    className='h-32px w-32px border-radius-8px'
                                    src={URL + material.fileUploadDto.filePath}
                                    alt={material?.name}
                                />
                            ) : (
                                <IconChipCircleTools className='h-32px w-32px icon-color-general-950' />
                            )}
                            <CustomTooltip
                                text={material?.name || itemSelected?.name || EMPTY_VALUES.DEFAULT}
                                classes={'font-color-general-950 fs-14px ml-2 text-ellipsis'}
                                showIfTruncated={true}
                                placement={'top'}
                            />
                        </div>
                        {/* Entry of the material, the one this exit closes. The detail carries it, the row is
                            the fallback while it loads //*/}
                        <div className={'d-flex align-items-center gap-2'}>
                            <IconEntryDevice className={'icon-color-shades-500 w-16px h-16px'} />
                            <p className={'ibl-font-w-400 font-color-shades-500 fs-14px m-0'}>
                                {t('entry') +
                                    ': ' +
                                    getDateFormat(
                                        detail?.accessDate || itemSelected?.accessDate,
                                        false,
                                        DATE_TIME_FORMAT_NAME_MODEL.shortMonthWithTime12,
                                    )}
                            </p>
                        </div>
                    </div>
                </div>

                <div className={'border-bottom-shades-300'} />
                <div className={'row my-4'}>
                    <div className='col-12'>
                        <CustomDateTimePicker
                            id={'dp_mcom_date_time'}
                            name={'accessDate'}
                            onChange={(e) => {
                                handleChange({ target: { value: e, name: 'accessDate' } });
                            }}
                            trans={'exit'}
                            value={item?.accessDate}
                            required={true}
                            disablePast={false}
                            disableFuture={true}
                            emptyFields={emptyFields}
                        />
                    </div>

                    <div className='col-12'>
                        <CustomAddChipSearch
                            autocompleteId={'slt_mcom_employee'}
                            buttonId={'btn_mcom_employee'}
                            label='employee'
                            selectedList={selectedList}
                            setSelectedList={setSelectedList}
                            // One option per entry left without its exit, so an employee that entered on more
                            // than one day appears once per entry and the guard picks which one this exit
                            // closes. Beside the name goes the date and time of the entry, which is what tells
                            // them apart and what stays visible on the chip //
                            sanitizeDataList={employeeOpenEntryListToLabel}
                            api={SERVICE_ADVANCED_SEARCH_SIMPLE_SEARCH_USER}
                            bodyApi={getModelSimpleSearch(TABLES.USER_INSIDE_BY_CONTRACTOR_ID, {
                                userId: detail?.contractorId || itemSelected?.contractorId,
                                userTypeIds: [STATUS_MODEL.ACTIVE], // userTypeIds is used as statusIds by this model
                            })}
                            keyId={'entryId'}
                            maxLength={1}
                            required={true}
                            emptyFields={emptyFields}
                            showSubSection={true}
                            completeWidth={true}
                        />
                    </div>

                    <div className={'col-12 pt-4'}>
                        <p className='text-center ibl-font-w-700 text-uppercase'>
                            {t('please_provide_a_brief_reason_related_to_this_check_out')}
                        </p>
                    </div>

                    <div className='col-12 pt-4'>
                        <CustomTextField
                            id='txt_mcom_note'
                            length={512}
                            name='note'
                            value={item?.note}
                            trans='note'
                            type='text'
                            onChange={handleChange}
                            multiline={true}
                        />
                    </div>
                </div>
            </CustomCard>
        </CustomModal>
    );
};

export default MaterialCheckOutModal;
